import { useCallback, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';

import { getAbTestDateRange } from '../ab-test-config/utils';
import AbTestConversationMetricCards from './AbTestConversationMetricCards';
import ChatsTable from 'src/components/dashboard-pages/solve-insights/chats/ChatsTable';
import {
  API,
  SLACK,
  VOICE,
  WEB_CALL,
} from 'src/components/dashboard-pages/solve-insights/constants';
import { dateRangeToTimestamp } from 'src/components/dashboard-pages/solve-insights/helpers';
import { useStateParams } from 'src/hooks/hooks';
import { SolveABTestVariant } from 'src/services/agent-ab-test/types';
import {
  dateRangeDeserialize,
  dateRangeSerialize,
  timeFilterParameterValidator,
} from 'src/utils/discover/helpers';
import { last30DaysTimeRange } from 'src/utils/timeRangeHelpers';

interface AbTestConversationsTabProps {
  abTestId: string;
  channel?: string;
  endDatetime?: string;
  intentWorkflowId: string;
  startDatetime?: string;
  timezone?: string;
  variants: SolveABTestVariant[];
}

const AbTestConversationsTab = ({
  abTestId,
  channel,
  endDatetime,
  intentWorkflowId,
  startDatetime,
  timezone,
  variants,
}: AbTestConversationsTabProps) => {
  const abTestDateRange = useMemo(
    () =>
      getAbTestDateRange(startDatetime, endDatetime, timezone) ??
      last30DaysTimeRange,
    [startDatetime, endDatetime, timezone],
  );

  const [dateRange, setDateRange] = useStateParams({
    deserialize: dateRangeDeserialize,
    initialState: abTestDateRange,
    paramsName: 'date',
    serialize: dateRangeSerialize,
    validator: timeFilterParameterValidator([]),
  });

  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const scrollToTop = useCallback((yOffset: number, smooth = false) => {
    if (!containerRef.current) {
      return;
    }
    if (containerRef.current.scrollTop > yOffset) {
      containerRef.current.scrollTo({
        behavior: smooth ? 'smooth' : 'auto',
        top: yOffset,
      });
    }
  }, []);

  const initialMultiSelected = useMemo(
    () =>
      variants.map(
        v =>
          `workflow_versions.${intentWorkflowId}:${v.intent_workflow_version}`,
      ),
    [intentWorkflowId, variants],
  );

  const { end_timestamp, start_timestamp } = dateRangeToTimestamp(dateRange);

  return (
    <Box
      onScroll={e => {
        setScrollTop(e.currentTarget.scrollTop);
      }}
      ref={containerRef}
      sx={{
        bgcolor: theme => theme.palette.background.paper,
        height: 'calc(100% - 2px)',
        overflow: 'auto',
        padding: '0 40px',
        paddingTop: '24px',
      }}
    >
      <AbTestConversationMetricCards
        abTestId={abTestId}
        endTimestamp={end_timestamp}
        startTimestamp={start_timestamp}
      />
      <ChatsTable
        abTestConfigId={abTestId}
        channel={channel}
        dateRange={dateRange}
        density='comfortable'
        initialMultiSelected={initialMultiSelected}
        isApiChannel={channel === API}
        isDetail
        isSlackChannel={channel === SLACK}
        isVoiceChannel={channel === VOICE}
        isWebCallChannel={channel === WEB_CALL}
        scope='main'
        scrollTop={scrollTop}
        scrollToTop={scrollToTop}
        setDateRange={setDateRange}
        view='ab_tests'
      />
    </Box>
  );
};

export default AbTestConversationsTab;
